import React, { useContext, useState } from "react";
import { ConnectedInstanceAddForm } from "./ConnectedInstanceAddForm";
import { useMutation } from "@apollo/client";
import {
  CREATE_INSTANCE_MUTATION,
  CreateInstanceData,
  CreateInstanceVars,
  UPDATE_INSTANCE_MUTATION,
  UpdateInstanceData,
  UpdateInstanceVars,
} from "../../../../graphql/mutations/instances";
import { useTranslation } from "next-i18next";
import { subdomainSlugify } from "../subdomainSlugify";
import {
  SETUP_SERVICE_MUTATION,
  SetupServiceData,
} from "../../../../graphql/mutations/applications";
import { useRouter } from "next/router";
import { Toast } from "../../../../components/TailwindControls/Toast";
import { accountsContext } from "../../../../shared/apolloClient";
import { mutationResponseHandler } from "../../../../shared/serverResponseHandlingUtils";
import { cookieUserStore } from "../../../../stores/Cookies/CookieUserStore";
import { ICRMConnectedApps, ICRMInstance } from "../../../../models/Accounts";
import { InstanceStoreContextCRM } from "../../../../stores/Instances/InstanceStoreCRM";
import { InstanceStoreContextVSign } from "../../../../stores/Instances/InstanceStoreVSign";
import { vSignCreateInstance } from "../../../../external-integration-api/v-sign-api/vSignInstanceCRUD";
import { SupportedApps } from "../../../../models/shared";
import {
  projectManagementCreateInstance,
  projectManagementUpdateInstance,
} from "../../../../external-integration-api/project-management/projectManagementInstanceCRUD";
import { InstanceStoreContextProjectManagement } from "../../../../stores/Instances/InstanceStoreProjectManagement";

export const InstanceAdd = ({
  renderFullPage = false,
  itemsCount = 0,
  onOutsideClick,
  handleButtonClose,
  setInstanceItemsCount = () => {},
}: {
  renderFullPage?: boolean;
  itemsCount?: number;
  handleButtonClose: () => void;
  onOutsideClick: (value: boolean) => void;
  setInstanceItemsCount?: (value: number) => void;
}) => {
  const user = cookieUserStore.getUserDetails();
  const { t } = useTranslation("instances");
  const router = useRouter();
  const servicesToSetup = ["crm", "accounts", "workflow", "notify"];
  const { instances, addInstance } = useContext(InstanceStoreContextCRM);
  const { addVSignInstance } = useContext(InstanceStoreContextVSign);
  const { addProjectManagementInstance, getByProjectManagementInstanceId } =
    useContext(InstanceStoreContextProjectManagement);
  const [loadingState, setLoadingState] = useState(false);
  const [currentSubdomain, setCurrentSubdomain] = useState("");
  const [processCompleted, setProcessCompleted] = useState(false);
  const [createdInstance, setCreatedInstance] =
    useState<Partial<ICRMInstance> | null>(null);
  const [setupService] = useMutation<SetupServiceData>(SETUP_SERVICE_MUTATION);
  const [steps, setSteps] = React.useState(0);
  const [selectedApp, setSelectedApp] = React.useState(["crm"]);

  const handleAppSelection = (app: string) => {
    let newSelectedApp = [];
    if (selectedApp.includes(app)) {
      newSelectedApp = selectedApp.filter((sApp) => sApp !== app);
      setSelectedApp(newSelectedApp);
    } else {
      setSelectedApp([...selectedApp, app]);
    }
  };

  const [createInstance] = useMutation<CreateInstanceData, CreateInstanceVars>(
    CREATE_INSTANCE_MUTATION,
    {
      onCompleted: (responseOnCompletion) => {
        if (
          responseOnCompletion?.createInstance?.messageKey?.includes("-success")
        ) {
          addInstance(responseOnCompletion?.createInstance?.data);
          setInstanceItemsCount(itemsCount + 1);
        }
      },
    }
  );

  const [UpdateInstance] = useMutation<UpdateInstanceData, UpdateInstanceVars>(
    UPDATE_INSTANCE_MUTATION,
    {
      fetchPolicy: "no-cache",
      context: {
        headers: {
          vrynopath: SupportedApps.accounts,
        },
      },
      onCompleted: (responseOnCompletion) => {
        if (responseOnCompletion?.updateInstance?.data) {
          addInstance(responseOnCompletion?.updateInstance?.data);
        }
      },
    }
  );

  const createAndValidateInstance = async (
    values: Partial<ICRMInstance>,
    subdomain: string,
    selectedApp: string[]
  ) => {
    const connectedAppsData: ICRMConnectedApps = {
      crm: { name: "CRM", active: true },
      sign: { name: "SIGN", active: false },
      projects: { name: "PROJECTS", active: false },
    };
    selectedApp.forEach((app) => {
      if (connectedAppsData[app as keyof typeof connectedAppsData]) {
        connectedAppsData[app as keyof typeof connectedAppsData].active = true;
      }
    });

    const { data, errors } = await createInstance({
      variables: {
        instance: {
          name: values.name,
          subdomain,
          description: values.description ? values.description : "",
          instanceAdmins: values.instanceAdmins,
          isSample: values.isSample,
          region: "in",
          connectedApps: connectedAppsData,
          newSampleDataPopulated: true,
        },
      },
      ...accountsContext,
    });

    const actualResponse = mutationResponseHandler(
      errors,
      data,
      t,
      (input) => input?.createInstance
    );
    if (!actualResponse) return;

    let vSignInstanceResponse = null;
    // if (vSignInstanceStatus === "subscribed") {
    vSignInstanceResponse = await vSignCreateInstance(
      actualResponse.id,
      actualResponse.subdomain,
      actualResponse.name,
      actualResponse.description,
      actualResponse.instanceAdmins,
      true,
      connectedAppsData.sign.active
    );
    // }

    const pmInstanceFromStore = getByProjectManagementInstanceId(
      actualResponse.subdomain
    );
    let projectManagementInstanceResponse = null;
    if (pmInstanceFromStore) {
      projectManagementInstanceResponse = await projectManagementUpdateInstance(
        actualResponse.id,
        actualResponse.subdomain,
        actualResponse.name,
        actualResponse.description,
        actualResponse.instanceAdmins,
        true,
        connectedAppsData.sign.active,
        connectedAppsData.projects.active
      );
    } else {
      projectManagementInstanceResponse = await projectManagementCreateInstance(
        actualResponse.id,
        actualResponse.subdomain,
        actualResponse.name,
        actualResponse.description,
        actualResponse.instanceAdmins,
        true,
        connectedAppsData.sign.active,
        connectedAppsData.projects.active
      );
    }

    if (vSignInstanceResponse) {
      addVSignInstance(vSignInstanceResponse);
    }
    if (projectManagementInstanceResponse) {
      addProjectManagementInstance(projectManagementInstanceResponse);
    }

    if (!vSignInstanceResponse || !projectManagementInstanceResponse) {
      UpdateInstance({
        variables: {
          id: actualResponse?.id,
          instance: {
            connectedApps: {
              crm: { name: "CRM", active: true },
              sign: {
                name: "SIGN",
                active: !vSignInstanceResponse
                  ? // Note: Settings this false here because it's instance add, and if there is no response from vSign instance creation,
                    // it means instance is not created in vSign, thus setting active as false. (Irrespective of whether user selected vSign or not
                    // cz if user did not select vSign then also there will be no response from vSign instance creation, so in both cases setting active as false.)
                    false
                  : connectedAppsData.sign.active,
              },
              projects: {
                name: "PROJECTS",
                active: !projectManagementInstanceResponse
                  ? // Same as vSign
                    false
                  : connectedAppsData.projects.active,
              },
            },
          },
        },
      });
    }
    return actualResponse;
  };

  const handleInstanceCreation = async (values: Partial<ICRMInstance>) => {
    setLoadingState(true);
    const subdomain = values?.subdomain
      ? subdomainSlugify(values.subdomain)
      : "";
    try {
      const response = await createAndValidateInstance(
        values,
        subdomain,
        selectedApp
      );
      // instance creation failed
      if (!response) {
        setLoadingState(false);
        setSteps(1);
        return;
      }
      setCreatedInstance(response);

      const allServicesSetup = await Promise.all(
        servicesToSetup.map((oneService) => {
          return setupService({
            context: {
              headers: {
                vrynopath: oneService,
                subdomain: subdomain,
              },
            },
          });
        })
      );
      const allServiceData = allServicesSetup
        .map(
          (oneResponse) =>
            !!mutationResponseHandler(
              oneResponse.errors,
              oneResponse.data,
              t,
              (input) => input?.setupService
            )
        )
        .reduce((acc, curr) => acc && curr);

      setCurrentSubdomain(subdomain);
      //TODO: set correct status and error handling
      setProcessCompleted(true);
    } catch (error) {
      console.error(error);
      Toast.error(t("common:unknown-message"));
      router.replace("/instances");
      return handleButtonClose();
    }
  };

  return (
    <ConnectedInstanceAddForm
      steps={steps}
      instance={
        createdInstance || {
          instanceAdmins: [user ? user.email : ""],
          name: "",
          subdomain: "",
        }
      }
      instances={instances}
      loading={loadingState}
      processCompleted={processCompleted}
      currentSubdomain={currentSubdomain}
      renderFullPage={renderFullPage}
      selectedApp={selectedApp}
      setSteps={setSteps}
      handleSubmit={handleInstanceCreation}
      onOutsideClick={onOutsideClick}
      handleButtonClose={handleButtonClose}
      handleAppSelection={handleAppSelection}
    />
  );
};
